import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAsk } from '../components/Ask';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { api, ApiError } from '../lib/api';
import type { AssignablePerson, Board, BoardColumn, WorkItemCard } from '../lib/types';
import { BoardCard } from './BoardCard';
import { WorkItemDrawer } from './WorkItemDrawer';
import { AddCardForm } from './AddCardForm';
import { TaskTable } from './TaskTable';
import { GanttPage } from '../plan/routes/schedule/Gantt';
import { MindMapPage } from '../plan/routes/schedule/MindMap';
import { PlanBoundary } from '../plan/PlanBoundary';
import { WorkFilters } from '../components/WorkFilters';
import { useSession } from '../lib/session';
import type { BoardFilter } from '../lib/boardFilter';
import { EMPTY_FILTER, applyFilter, countItems, dedupe, isFiltering } from '../lib/boardFilter';

/** Three readings of one board: sections, columns, or dates. Remembered per person. */
/**
 * `timeline` now means the imported Gantt rather than the hand-written strip it replaces,
 * and `diagram` is the mind map beside it. The value is kept as `timeline` so a bookmarked
 * ?view=timeline still lands somewhere sensible.
 */
type BoardView = 'table' | 'kanban' | 'timeline' | 'diagram';

const BOARD_VIEWS: BoardView[] = ['table', 'kanban', 'timeline', 'diagram'];

/** One list, so a new view cannot be added to the tabs and forgotten here. */
function isBoardView(value: string | null): value is BoardView {
  return value !== null && (BOARD_VIEWS as string[]).includes(value);
}

/**
 * THE FOUR VIEW ICONS, DRAWN ON ONE GRID.
 *
 * They were Unicode glyphs — ▤ ▥ ▬ ◈ — and no amount of CSS was going to line those up. They
 * come from different parts of the Geometric Shapes block and carry different metrics: the two
 * squares are close to full height and sit on the baseline, the black rectangle is a short wide
 * bar centred on the x-height, and the diamond is drawn larger than its neighbours. So each one
 * landed at its own height, and the set drifted again with whatever font the machine fell back to.
 *
 * Same 16×16 box, same 1.5px stroke, same optical weight, and `currentColor` so they take the
 * tab's own colour when it is selected. Each says what its view is: rows, columns, bars on a
 * timeline, boxes joined up.
 */
function ViewIcon({ of }: { of: 'table' | 'kanban' | 'timeline' | 'diagram' }) {
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    /* Nudges the box onto the text's optical centre; `align-items: center` centres the line
       box, which is taller than the letters. */
    style: { display: 'block', flexShrink: 0 },
  };

  if (of === 'table') {
    return (
      <svg {...common}>
        <rect x="2" y="2.75" width="12" height="10.5" rx="1.5" />
        <path d="M2 6.25h12M2 9.75h12" />
      </svg>
    );
  }
  if (of === 'kanban') {
    return (
      <svg {...common}>
        <rect x="2" y="2.75" width="12" height="10.5" rx="1.5" />
        <path d="M6 2.75v10.5M10 2.75v10.5" />
      </svg>
    );
  }
  if (of === 'timeline') {
    return (
      <svg {...common}>
        <path d="M2.5 4.5h7M4.5 8h8M2.5 11.5h5.5" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <rect x="1.75" y="6" width="5" height="4" rx="1" />
      <rect x="9.25" y="2.5" width="5" height="4" rx="1" />
      <rect x="9.25" y="9.5" width="5" height="4" rx="1" />
      <path d="M6.75 8h1.25M9.25 4.5H8V8m0 0v3.5h1.25" />
    </svg>
  );
}

export function BoardPage({ projectId: given }: { projectId?: string } = {}) {
  /**
   * The project comes from the URL, or is handed in.
   *
   * Handed in by the Project management page, which renders this board under its own picker
   * so the kanban is one click from the sidebar instead of three. Everything else about the
   * board is identical — same data, same rules, same task panel in the address bar.
   */
  const { projectId: fromUrl } = useParams<{ projectId: string }>();
  const projectId = given ?? fromUrl;
  const [board, setBoard] = useState<Board | null>(null);
  const ask = useAsk();
  const [people, setPeople] = useState<AssignablePerson[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  /**
   * Which task is open is held in the URL rather than in state, so a notification can link
   * straight to one, the address is shareable, and Back closes the panel.
   */
  const openTaskId = searchParams.get('task');

  function openTask(item: WorkItemCard | string) {
    const id = typeof item === 'string' ? item : item.id;
    const next = new URLSearchParams(searchParams);
    next.set('task', id);
    setSearchParams(next, { replace: false });
  }

  function closeTask() {
    const next = new URLSearchParams(searchParams);
    next.delete('task');
    setSearchParams(next, { replace: false });
  }
  const [dragging, setDragging] = useState<WorkItemCard | null>(null);
  const [addingTo, setAddingTo] = useState<string | null>(null);
  /**
   * Bumped on every reload and used as the board's key. dnd-kit leaves a transform on the
   * cards a dragged item left behind, and because React reuses those nodes across a reload
   * the stale offset would survive; remounting clears it.
   */
  const [revision, setRevision] = useState(0);
  const { session } = useSession();
  const [showClosed, setShowClosed] = useState(false);

  /**
   * Remembered per project: coming back to a board you had narrowed and finding it wide open
   * again is the sort of thing that stops people using a filter at all. The bar always says
   * how many of how many are showing, so a remembered filter can never be mistaken for an
   * empty project.
   */
  const [filter, setFilter] = useState<BoardFilter>(EMPTY_FILTER);
  /**
   * The URL wins, then whichever view this person last worked in.
   *
   * The URL had not been read at all, so `?view=timeline` in a link somebody sent landed on
   * whatever the recipient happened to have open last — and `diagram` was missing from the
   * remembered list, so choosing it and reloading quietly dropped back to the table.
   */
  const [view, setView] = useState<BoardView>(() => {
    const asked = searchParams.get('view');
    if (isBoardView(asked)) return asked;
    try {
      const saved = localStorage.getItem('hub.boardView');
      return isBoardView(saved) ? saved : 'table';
    } catch {
      return 'table';
    }
  });

  /**
   * The two tabs that render the imported plan screens. They behave differently from the
   * board's own views in two ways worth naming in one place: they fetch their own payload,
   * and they carry their own filtering.
   */
  const isPlanView = view === 'timeline' || view === 'diagram';

  function chooseView(next: BoardView) {
    setView(next);
    /**
     * Kept in the URL as well as remembered, because the URL is now read on mount: leaving a
     * stale `?view=` behind while the tabs moved on would send a reload back to whichever
     * tab the link was written for.
     */
    const params = new URLSearchParams(searchParams);
    params.set('view', next);
    setSearchParams(params, { replace: true });
    try {
      localStorage.setItem('hub.boardView', next);
    } catch {
      /* private browsing */
    }
  }

  const filterKey = projectId ? `hub.boardFilter.${projectId}` : null;

  useEffect(() => {
    if (!filterKey) return;
    try {
      const saved = localStorage.getItem(filterKey);
      setFilter(saved ? { ...EMPTY_FILTER, ...JSON.parse(saved) } : EMPTY_FILTER);
    } catch {
      setFilter(EMPTY_FILTER);
    }
  }, [filterKey]);

  function changeFilter(next: BoardFilter) {
    setFilter(next);
    if (!filterKey) return;
    try {
      if (isFiltering(next)) localStorage.setItem(filterKey, JSON.stringify(next));
      else localStorage.removeItem(filterKey);
    } catch {
      /* private browsing */
    }
  }

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const [boardResult, peopleResult] = await Promise.all([
        api.get<Board>(
          `/projects/${projectId}/board?${new URLSearchParams({
            ...(showClosed ? { includeClosed: 'true' } : {}),
          })}`,
        ),
        api.get<AssignablePerson[]>(`/projects/${projectId}/assignable`).catch(() => []),
      ]);
      setBoard(boardResult);
      setPeople(peopleResult);
      setRevision((n) => n + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the board');
    }
  }, [projectId, showClosed]);

  useEffect(() => {
    void load();
  }, [load]);

  const sensors = useSensors(
    // A small distance threshold keeps a click-to-open from being read as a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const cardsById = useMemo(() => {
    const map = new Map<string, WorkItemCard>();
    board?.columns.forEach((column) => column.items.forEach((item) => map.set(item.id, item)));
    return map;
  }, [board]);

  const canWrite = board?.capabilities['work.write'] !== 'none';
  /**
   * Moving a card is its own permission, and not the same as changing one.
   *
   * A customer says plenty about the work — the wording, the type, the priority they are
   * asking for — and says none of it by dragging a card into Done. So the cards stay
   * draggable for us and are fixed for them, rather than offering a drag the API refuses.
   */
  const canMove = board?.capabilities['work.move'] !== 'none';

  function onDragStart(event: DragStartEvent) {
    setDragging(cardsById.get(String(event.active.id)) ?? null);
  }

  async function onDragEnd(event: DragEndEvent) {
    const card = cardsById.get(String(event.active.id));
    setDragging(null);
    if (!card || !event.over || !board) return;

    // The droppable is either a column or another card; resolve both to a column.
    const overId = String(event.over.id);
    const targetColumn =
      board.columns.find((column) => column.id === overId) ??
      board.columns.find((column) => column.items.some((item) => item.id === overId));
    if (!targetColumn) return;

    const siblings = targetColumn.items.filter((item) => item.id !== card.id);
    const overIndex = siblings.findIndex((item) => item.id === overId);
    const position = overIndex === -1 ? siblings.length : overIndex;

    if (card.statusId === targetColumn.id && card.position === position) return;

    // Blocked needs a reason and Done needs a resolution note; the API enforces both, so ask
    // rather than let the drop fail.
    let reason: string | undefined;
    if (targetColumn.category === 'blocked' && card.statusId !== targetColumn.id) {
      const answer = await ask.prompt({
        title: `Why is ${card.key} blocked?`,
        body: 'Whoever picks this up next reads this, so say what it is waiting on.',
        label: 'What is holding it up',
        placeholder: 'Waiting on the customer to confirm the account numbers',
        confirmLabel: 'Mark it blocked',
        minLength: 3,
      });
      if (answer === null) return;
      if (!answer.trim()) {
        setError('A blocked item needs a reason.');
        return;
      }
      reason = answer.trim();
    }
    if (
      targetColumn.category === 'done' &&
      card.statusId !== targetColumn.id &&
      card.sla &&
      !card.resolution.trim()
    ) {
      const answer = await ask.prompt({
        title: `What was done to resolve ${card.key}?`,
        body: 'This is service work, so the resolution is part of the record — and it is what the next person reads when the same thing comes back.',
        label: 'What fixed it',
        placeholder: 'Restarted the export worker and cleared the stuck batch',
        confirmLabel: 'Resolve it',
        minLength: 3,
      });
      if (answer === null) return;
      if (!answer.trim()) {
        setError('Service work needs a note saying what was done.');
        return;
      }
      reason = answer.trim();
    }

    // Move it locally first so the board does not lurch, then reconcile with the server.
    const previous = board;
    setBoard(applyMove(board, card, targetColumn.id, position));
    setError(null);
    try {
      await api.put(`/work-items/${card.id}/move`, { statusId: targetColumn.id, position, reason });
      await load();
    } catch (err) {
      setBoard(previous);
      setError(err instanceof ApiError ? err.message : 'Could not move that item');
    }
  }

  if (error && !board) return <div className="callout">{error}</div>;
  if (!board) return <div className="empty">Loading board…</div>;

  /* Both client roles are the customer's side: the lead edits more, but sees the same board. */
  const isClient = (board.role === 'client' || board.role === 'client_admin') && !session?.person.isAdmin;

  /**
   * WHO MAY PUT A NEW CARD ON THE BOARD.
   *
   * Not the same question as `work.write`, which is why this is asked by ROLE and not by
   * reaching for some staff-ish capability as a stand-in — the argument is the one written all
   * over `access.ts`: a capability must never be used as a proxy for whose side somebody is on.
   *
   * A customer must always be able to reach us, and they still can: "Report it" raises work
   * through a form that asks the questions we need — what kind, who it affects, how badly —
   * and lands it in the same place. What is taken away is only the board's own blank
   * "+ Add item", which invites a customer to file work into OUR columns with none of that
   * asked. The board is where we organise what they have raised; it is not the front door.
   */
  const canAddCards = canWrite && !isClient;

  const total = countItems(board);
  /**
   * One narrowing, applied once, handed to whichever view is showing. The views take a Board
   * and do not know a filter exists, so the three of them cannot disagree about what matched.
   */
  const shown = applyFilter(board, filter, session?.person.id ?? null);
  const shownCount = countItems(shown);

  /**
   * Built from the unfiltered board, so a choice does not vanish the moment you use it, and
   * only from what is actually on a card here — the whole company's people would be noise.
   */
  const facets = {
    people: dedupe(
      board.columns.flatMap((column) => column.items.flatMap((item) => item.assignees)),
      (person) => person.id,
    ).sort((a, b) => a.name.localeCompare(b.name)),
    categories: dedupe(
      board.columns.flatMap((column) => column.items.flatMap((item) => (item.category ? [item.category] : []))),
      (category) => category.id,
    )
      .map((category) => ({ id: category.id, path: category.path }))
      .sort((a, b) => a.path.localeCompare(b.path)),
    groups: board.groups.map((group) => ({ id: group.id, name: group.name })),
  };

  return (
    <>
      <div className="page-head">
        <div>
          {/*
            Company then project, not just the project.
            The eyebrow used to name the project alone, so from a board the only way back to
            the customer's dashboard was project overview first — and from the roadmap there
            was no way back at all. A breadcrumb is one line and removes both dead ends.
          */}
          <div className="eyebrow crumbs">
            {/*
              Only when the board is embedded in Project management, and first in the trail:
              it is where this person actually came from, so it is where back should go.
            */}
            {given && (
              <>
                <Link to="/project-management" className="muted">
                  Project management
                </Link>
                <span aria-hidden>›</span>
              </>
            )}
            <Link to={`/companies/${board.project.companyId}`} className="muted">
              {/* The name from the session, which already has it — no round trip for a word. */}
              {session?.companies.find((c) => c.id === board.project.companyId)?.name ?? 'Company'}
            </Link>
            <span aria-hidden>›</span>
            <Link to={`/projects/${board.project.id}`} className="muted">
              {board.project.name}
            </Link>
          </div>
          <h1>{board.project.name}</h1>
          <p className="subtitle">
            {view === 'table'
              ? `${board.groups.length} section${board.groups.length === 1 ? '' : 's'}`
              : view === 'kanban'
                ? `${board.columns.length} columns`
                : view === 'diagram'
                  ? 'The plan as a tree'
                  : 'The plan on a Gantt'}
            {board.transitionsEnforced ? ' · this project has workflow rules' : ''}
            {canWrite ? '' : ' · you have read-only access here'}
            {canWrite && !canMove ? ' · the columns are ours to move' : ''}
          </p>
        </div>
        <div className="row">
          <div className="view-tabs" role="tablist" aria-label="Board view">
            <button
              type="button"
              role="tab"
              aria-selected={view === 'table'}
              className={view === 'table' ? 'view-tab active' : 'view-tab'}
              onClick={() => chooseView('table')}
            >
              <ViewIcon of="table" /> Table
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'kanban'}
              className={view === 'kanban' ? 'view-tab active' : 'view-tab'}
              onClick={() => chooseView('kanban')}
            >
              <ViewIcon of="kanban" /> Kanban
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'timeline'}
              className={view === 'timeline' ? 'view-tab active' : 'view-tab'}
              onClick={() => chooseView('timeline')}
            >
              <ViewIcon of="timeline" /> Timeline
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'diagram'}
              className={view === 'diagram' ? 'view-tab active' : 'view-tab'}
              onClick={() => chooseView('diagram')}
            >
              <ViewIcon of="diagram" /> Diagram
            </button>
          </div>
          {board.closedCount > 0 && !isPlanView && (
            <button
              type="button"
              className={showClosed ? 'btn btn-sm' : 'btn btn-quiet btn-sm'}
              onClick={() => setShowClosed(!showClosed)}
              title="Closed work is kept, just off the board"
            >
              {showClosed ? 'Hide' : 'Show'} {board.closedCount} closed
            </button>
          )}
          <Link to={`/projects/${board.project.id}`} className="btn">
            Overview
          </Link>
        </div>
      </div>

      {/* Hidden on the plan tabs: the Gantt and the mind map read their own payload, so
          none of this reaches them, and a search box that filters nothing is worse than no
          search box. The chart has its own Filter in its toolbar. */}
      {!isPlanView && (
        <WorkFilters
          facets={facets}
          filter={filter}
          onChange={changeFilter}
          shown={shownCount}
          total={total}
          meId={session?.person.id ?? null}
          placeholder="Find on this board — key, title, wording"
        />
      )}

      {error && (
        <div className="callout mb-2" role="alert">
          {error}
        </div>
      )}
      {notice && <div className="callout callout-ok mb-2">{notice}</div>}

      {isFiltering(filter) && shownCount === 0 ? (
        /**
         * One empty state for all three views. Without it a filter that matches nothing left
         * a blank page with only a small "0 of 12" in the bar to explain why.
         */
        <div className="card">
          <div className="empty">
            <h3>Nothing here matches</h3>
            <p>
              All {total} task{total === 1 ? '' : 's'} on this board are hidden by the filters above.
            </p>
            <button type="button" className="btn btn-primary" onClick={() => changeFilter({ ...EMPTY_FILTER })}>
              Clear the filters
            </button>
          </div>
        </div>
      ) : view === 'table' ? (
        <TaskTable
          board={shown}
          hideEmptySections={isFiltering(filter)}
          people={people}
          onOpen={openTask}
          onChanged={load}
          onError={setError}
        />
      ) : view === 'timeline' ? (
        /**
         * The Gantt from the port kit, in place of the strip that was here. It reads its own
         * payload from `/projects/:id/timeline` rather than the board, so the board's search
         * and facet filters do not reach it — which is why they are hidden on this tab and
         * the chart carries its own Filter box instead.
         */
        <PlanBoundary revision={revision}>
          <GanttPage scopeTo={board.project.id} onOpenTask={openTask} />
        </PlanBoundary>
      ) : view === 'diagram' ? (
        <PlanBoundary revision={revision}>
          <MindMapPage scopeTo={board.project.id} onOpenTask={openTask} />
        </PlanBoundary>
      ) : (
      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className="board" key={revision}>
          {shown.columns.map((column) => (
            <Column
              key={column.id}
              column={column}
              canAdd={canAddCards}
              canMove={!!canMove}
              adding={addingTo === column.id}
              people={people}
              onAdd={() => setAddingTo(column.id)}
              onCancelAdd={() => setAddingTo(null)}
              onAdded={async () => {
                setAddingTo(null);
                await load();
              }}
              onOpen={openTask}
              projectId={board.project.id}
            />
          ))}
        </div>

        <DragOverlay>
          {dragging ? (
            <div className="board-card" style={{ cursor: 'grabbing' }}>
              <span className="key">{dragging.key}</span>
              <div className="board-card-title">{dragging.title}</div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      )}

      {board.unassigned.length > 0 && (
        <div className="card mt-2">
          <div className="card-head">
            <h2>Without a column</h2>
            <span className="dim" style={{ fontSize: 12 }}>
              Their column was deleted — drag them back onto the board
            </span>
          </div>
          <div className="card-pad stack-sm">
            {board.unassigned.map((item) => (
              <button key={item.id} type="button" className="board-card" onClick={() => openTask(item)}>
                <span className="key">{item.key}</span> {item.title}
              </button>
            ))}
          </div>
        </div>
      )}

      {openTaskId && (
        <WorkItemDrawer
          itemId={openTaskId}
          companyId={board.project.companyId}
          people={people}
          columns={board.columns}
          onClose={closeTask}
          onChanged={async (message) => {
            setNotice(message ?? null);
            await load();
          }}
          onDeleted={async () => {
            closeTask();
            await load();
          }}
        />
      )}
    </>
  );
}

function Column({
  column,
  canAdd,
  canMove,
  adding,
  people,
  projectId,
  onAdd,
  onCancelAdd,
  onAdded,
  onOpen,
}: {
  column: BoardColumn;
  /** Raising work on the board is ours; a customer's route is "Report it". See `canAddCards`. */
  canAdd: boolean;
  canMove: boolean;
  adding: boolean;
  people: AssignablePerson[];
  projectId: string;
  onAdd: () => void;
  onCancelAdd: () => void;
  onAdded: () => void;
  onOpen: (item: WorkItemCard) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  return (
    <section className={isOver ? 'board-column is-over' : 'board-column'}>
      <header className="board-column-head" style={{ background: 'var(--surface-sunken)' }}>
        <span className="dot" style={{ background: column.color }} />
        <span className="board-column-title">{column.label}</span>
        <span className="board-column-count">{column.items.length}</span>
      </header>

      <div ref={setNodeRef} className="board-column-body">
        <SortableContext items={column.items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
          {column.items.map((item) => (
            <BoardCard key={item.id} item={item} draggable={canMove && item.editable} onOpen={onOpen} />
          ))}
        </SortableContext>
        {column.items.length === 0 && !adding && (
          <p className="dim" style={{ fontSize: 12, textAlign: 'center', margin: '14px 0' }}>
            Nothing here
          </p>
        )}
      </div>

      {canAdd &&
        (adding ? (
          <AddCardForm projectId={projectId} statusId={column.id} people={people} onCancel={onCancelAdd} onAdded={onAdded} />
        ) : (
          <button type="button" className="board-add" onClick={onAdd}>
            + Add item
          </button>
        ))}
    </section>
  );
}

/** Optimistic reordering that mirrors what the server does, so the board does not jump. */
function applyMove(board: Board, card: WorkItemCard, targetStatusId: string, position: number): Board {
  const columns = board.columns.map((column) => ({
    ...column,
    items: column.items.filter((item) => item.id !== card.id),
  }));
  const target = columns.find((column) => column.id === targetStatusId);
  if (!target) return board;

  const moved = { ...card, statusId: targetStatusId };
  target.items = [...target.items.slice(0, position), moved, ...target.items.slice(position)].map((item, index) => ({
    ...item,
    position: index,
  }));
  return { ...board, columns };
}
